import { useRef, useState, useCallback } from 'react'
import QRCode from 'react-qr-code'
import { Alert, Button, Card, CardBody, IconButton, Typography } from '@material-tailwind/react'
// @ts-expect-error - No types available for react-copy-to-clipboard
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { PiTrash, PiDownloadSimple, PiDetective, PiClipboard, PiCheckBold } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { formatDateTimeString } from '../utils/helpers/dates.helpers'
import { theme } from '../tailwind.config'
import { downloadPng } from '../utils/helpers/download.helpers'
import DeleteAccessCodeDialog from './DeleteAccessCodeDialog'
import { getRouteBaseUrl } from '../utils/helpers/url.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AccessCode, AgamiExperience } from '../types/supabase'

interface AccessCodePreviewCardProps {
  accessCode: AccessCode
  experienceList: AgamiExperience[]
  refetchCodesList: () => void
}

const AccessCodePreviewCard: React.FC<AccessCodePreviewCardProps> = ({
  accessCode,
  experienceList,
  refetchCodesList,
}) => {
  const { t } = useTranslation()
  const { colors: themeColors } = theme.extend

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const { darkMode } = useDarkMode()

  // find exp name from ID
  const experience = experienceList.find((exp) => exp.id === accessCode.experience_id)

  const expNameUrlComp = experience?.name ? `&expName=${encodeURIComponent(experience?.name)}` : ''

  const expLangUrlComp = experience?.main_language
    ? `&lang=${encodeURIComponent(experience?.main_language)}`
    : ''

  const pageUrl = accessCode.anonymous_access ? '/redeem-experience-anonymous' : '/redeem-experience'

  const routeUrl = `${getRouteBaseUrl()}${pageUrl}?code=${encodeURIComponent(accessCode.code ?? '')}${expNameUrlComp}${expLangUrlComp}`

  const qrCodeRef = useRef(null)
  const qrFramePadding = 12
  const qrFrameColor = darkMode ? themeColors['base-gray'][500] : themeColors['base-gray'][400]

  const experienceNotAvailable = experience === undefined

  const downloadTitle = experience?.name || accessCode.code || 'Access Code'

  const downloadQr = useCallback(() => {
    downloadPng({
      elementRef: qrCodeRef,
      title: downloadTitle,
      padding: qrFramePadding,
      frameColor: qrFrameColor,
    })
  }, [downloadTitle, qrFrameColor, qrFramePadding])

  const manageDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDeleteDialogOpen(true)
  }, [])

  const expired = accessCode?.expires_at && Date.now() > new Date(accessCode?.expires_at).getTime()

  return (
    <>
      <DeleteAccessCodeDialog
        isOpen={deleteDialogOpen}
        setIsOpen={setDeleteDialogOpen}
        codeToDelete={accessCode}
        refetchCodesList={refetchCodesList}
      />

      <Card
        className={`w-full h-full flex flex-col ${experienceNotAvailable ? 'bg-gray-100 opacity-70' : ''}`}
        data-testclass="access-code-preview-card"
        data-experience-name={experience?.name ?? ''}
      >
        <CardBody className="flex flex-col w-full min-w-0">
          {experienceNotAvailable && (
            <Alert variant={darkMode ? 'filled' : 'ghost'} color="orange" className="mb-4">
              {t('accessCode.experienceDeleted')}
            </Alert>
          )}

          <div className="flex space-x-2 justify-between items-start mb-4">
            <div className="flex flex-col flex-1 gap-2 min-w-0">
              <Typography
                className="font-bold truncate"
                title={experience?.name ?? accessCode.code}
                data-testid="access-code-experience-name"
                data-testclass="access-code-preview-card-name"
              >
                {experience?.name ?? accessCode.code}
              </Typography>

              <Typography variant="small">
                {t('dashboard.created')}{' '}
                <strong className="text-black dark:text-base-gray-400">
                  {accessCode.created_at ? formatDateTimeString(accessCode.created_at) : '-'}
                </strong>
              </Typography>

              <Typography variant="small">
                {t('accessCode.expiresAt')}{' '}
                <strong className="text-black dark:text-base-gray-400" data-testid="access-code-expires-at">
                  {accessCode.expires_at
                    ? formatDateTimeString(accessCode.expires_at)
                    : t('accessCode.never')}
                </strong>
              </Typography>

              <Typography variant="small">
                {t('accessCode.accessExpiresAfter')}{' '}
                <strong className="text-black dark:text-base-gray-400">
                  {accessCode.access_expires_after
                    ? `${accessCode.access_expires_after} ${t('accessCode.days')}`
                    : t('accessCode.never')}
                </strong>
              </Typography>
            </div>

            <IconButton
              variant="text"
              size="sm"
              color="yellow"
              onClick={manageDelete}
              data-testid="access-code-delete-btn"
            >
              <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
            </IconButton>
          </div>

          {accessCode.anonymous_access && (
            <div className="mb-4">
              <Alert
                color="blue"
                variant={darkMode ? 'filled' : 'ghost'}
                icon={<PiDetective className="w-6 h-6 dark:text-base-gray-400" />}
              >
                <Typography variant="h3" className="dark:text-base-gray-400">
                  {t('accessCode.guestAccess.title')}
                </Typography>
                <Typography variant="small" className="dark:text-base-gray-500 dark:font-semibold">
                  {t('accessCode.guestAccess.description')}
                </Typography>
              </Alert>
            </div>
          )}

          {!experienceNotAvailable && (
            <div className="w-full md:w-2/3 h-auto rounded-xl shadow-lg py-4 px-2 mx-auto relative text-center bg-base-gray-400 dark:bg-base-gray-500 flex flex-col justify-center items-center gap-4">
              {expired && (
                <div className="absolute rounded-xl top-0 bottom-0 left-0 right-0 flex items-center justify-center bg-primary-500 bg-opacity-80">
                  <Typography className="font-bold text-3xl text-accent-500 dark:text-accent-500">
                    {t('accessCode.expired')}
                  </Typography>
                </div>
              )}

              <Typography
                className="font-bold text-primary-500 dark:text-primary-500 text-xl"
                data-testid="access-code-value"
              >
                {accessCode.code}
              </Typography>
              <QRCode
                data-url={routeUrl}
                className="w-full"
                fgColor={themeColors.primary[500]}
                bgColor={darkMode ? themeColors['base-gray'][500] : themeColors['base-gray'][400]}
                ref={qrCodeRef}
                value={routeUrl}
                title={`Route QR Code - ${accessCode.code}`}
                data-testid="access-code-qr"
              />
              <div className="w-full flex flex-col 3xl:flex-row justify-between items-center gap-2 3xl:gap-0">
                <Button
                  color={'primary' as colors}
                  size="sm"
                  onClick={downloadQr}
                  className="flex justify-center items-center gap-2 mx-auto"
                  data-testid="access-code-download-qr-btn"
                >
                  <PiDownloadSimple className="h-3 w-3" /> {t('accessCode.downloadQr')}
                </Button>
                <CopyToClipboard
                  text={routeUrl}
                  onCopy={() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                >
                  <Button
                    size="sm"
                    color={'primary' as colors}
                    className="flex justify-center items-center gap-2 mx-auto"
                    disabled={copied}
                    variant={copied ? 'filled' : 'outlined'}
                    data-testid="access-code-copy-link-btn"
                  >
                    {copied ? (
                      <>
                        <PiCheckBold className="h-3 w-3" /> {t('accessCode.copied')}
                      </>
                    ) : (
                      <>
                        <PiClipboard className="h-3 w-3" /> {t('accessCode.copyLink')}
                      </>
                    )}
                  </Button>
                </CopyToClipboard>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

export default AccessCodePreviewCard
